import { Event, TEvent } from "./Emitter";
import { extend, sortBy, without } from "lodash-es"
import Period from "./period"
import { IStorage, InMemoryStorage } from "./Storage";
import { useEffect, useState } from "react";

export type Dictionary<T> = { [id: string]: T | undefined }

export interface PeriodRequestInfo {
    toBeRequested: Period[];
}

export class BaseStore {
    public Changed: Event = new Event();

    protected useStore = (onChanged: () => void) => {
        useEffect(() => {
            this.Changed.register(onChanged);
            return () => this.Changed.unregister(onChanged);
        })
    }
    public useState = <T>(action: () => T) => {
        const x = useState<T>(action());
        this.useStore(() => {
            const result = action();
            x[1](result);
        });
        return x;
    }
    protected useWithActionBase: <T>(action: () => void, getter: () => T) => T | null = (action, getter) => {
        const [item, setItem] = useState(getter());
        this.useStore(() => {
            setItem(getter())
        });

        useEffect(() => {
            if (getter() == null) {
                action();
            }
        }, [])

        return item;
    }

}

export class AbstractStore<T> extends BaseStore {
    _saved: TEvent<T> = new TEvent<T>();
    protected _storage: IStorage<{ [key: string]: T }>;

    constructor(protected idSelector: (t: T) => string, storage: IStorage<any> = new InMemoryStorage<{ [key: string]: T }>()) {
        super();
        this._storage = storage;
    }

    allByKey() {
        const result: Dictionary<T> = {};
        for (const item of this.getall()) {
            result[this.idSelector(item)] = item;
        }
        return result;
    }

    protected useWithAction = (action: () => void, id: string | null) => {
        const [item, setItem] = useState<T | null>(id ? this.get(id) : null);
        this.useStore(() => {
            let data = id ? this.get(id) : null;
            setItem(data)
        });
        useEffect(() => {
            if (id) {
                const i = this.get(id);
                if (!i)
                    action();
                else
                    setItem(i);
            }
            else
                setItem(null)
        }, [id])
        return item;
    }

    protected useManyWithAction = (action: (ids: string[]) => void, ids: string[]) => {
        const [items, setItems] = useState<T[] | null>(this.getall());
        this.useStore(() => {
            let data = this.getall();
            setItems(data)
        });
        useEffect(() => {
            const uids = ids.filter(id => this.get(id) == null);
            action(uids)
        }, [])
        return items?.filter(x => ids.indexOf(this.idSelector(x)) != -1);
    }

    get(id: string) {
        var obj = this._storage.get();
        if (!obj)
            return null;
        return obj[id] || null;
    }
    add(item: T) {
        if (this._storage.get() === null)
            this._storage.set({});
        var id = this.idSelector(item);
        if (!id)
            throw "invalid item";
        this._storage.get()![id] = item;
        this._storage.save();
        this.Changed.emit();
        this._saved.emit(item);
    }
    getall(): T[] {
        var result: T[] = []
        for (var key in this._storage.get()!) {
            result.push(this._storage.get()![key]);
        }
        return result;
    }
    delete(id: string): void {
        if (this._storage.get() !== null) {
            delete this._storage.get()![id];
            this._storage.save();
            this.Changed.emit();
        }
    }
    deleteMany(ids: string[]) {
        if (this._storage.get() !== null) {
            ids.forEach(id => delete this._storage.get()![id], this);
            this._storage.save();
            this.Changed.emit();
        }
    }
    setAll(items: Array<T>): void {
        var obj: { [key: string]: T } = {};
        for (let item of items) {
            obj[this.idSelector(item)] = item;
        }
        this._storage.set(obj);
        this.Changed.emit();
    }
    update(item: T): void {
        if (this._storage.get() === null)
            this._storage.set({});
        var id = this.idSelector(item);
        if (!id)
            throw "invalid item";
        // if (this._storage.get() ![id] == undefined)
        //     throw "item not found";
        this._storage.get()![id] = item;
        this._storage.save();
        this.Changed.emit();
        this._saved.emit(item);
    }
    updateOrAddAll(items: T[]): void {
        if (this._storage.get() === null)
            this._storage.set({});
        var obj = this._storage.get()!;
        items.forEach(item => obj[this.idSelector(item)] = item, this);
        this._storage.save();
        this.Changed.emit();
    }
    updateOrAdd(item: T): void {
        if (this._storage.get() === null)
            this._storage.set({});
        this._storage.get()![this.idSelector(item)] = item;
        this._storage.save();
        this.Changed.emit();
        this._saved.emit(item);
    }
    extendOrAddAll(items: T[]): void {
        if (this._storage.get() === null)
            this._storage.set({});
        var obj = this._storage.get();
        items.forEach(item => {
            var id = this.idSelector(item);
            var oldItem = obj![id];
            if (!oldItem)
                obj![id] = item;
            else
                obj![id] = extend(oldItem, item) as T;
        }, this);
        this._storage.save();
        this.Changed.emit();
    }
    extendOrAdd(item: T): void {
        if (this._storage.get() === null)
            this._storage.set({});

        var id = this.idSelector(item);
        var oldItem = this._storage.get()![id];
        if (!oldItem)
            this._storage.get()![id] = item;
        else
            this._storage.get()![id] = extend(oldItem, item) as T;

        this._storage.save();
        this.Changed.emit();
    }
}

export class AbstractGetAllStore<T> extends AbstractStore<T>{
    isInitialized() {
        return this._storage.get() !== null;
    }
    clear() {
        if (this._storage.get() != null)
            this._storage.set(null);
    }

    protected useAllWithAction = (action: () => void, sort?: (item: T) => any, filter?: (item: T) => boolean) => {
        const [items, setItems] = useState<T[] | null>(this.isInitialized()
            ? filter
                ? this.getall().filter(filter)
                : this.getall()
            : null);

        this.useStore(() => {
            let data = filter
                ? this.getall().filter(filter)
                : this.getall();
            if (data && sort)
                data = sortBy(data, sort);
            setItems(data)
        });
        useEffect(() => {
            if (!this.isInitialized())
                action();
        }, [])
        return items;
    }

}

export class AbstractOptionalGetAllStore<T> extends AbstractStore<T>{
    protected initialized = false;
    isInitialized() {
        return this.initialized;
    }
    clear() {
        if (this._storage.get() != null)
            this._storage.set(null);
    }

    protected useAllWithAction = (action: () => void, sort?: (item: T) => any) => {
        const [items, setItems] = useState<T[] | null>(this.isInitialized() ? this.getall() : null);
        this.useStore(() => {
            let data = this.getall();
            if (data && sort)
                data = sortBy(data, sort);
            setItems(data)
        });
        useEffect(() => {
            if (!this.isInitialized())
                action();
        }, [])
        return items;
    }

}

export class AbstractLookupStore<T> extends BaseStore {
    _saved: TEvent<T[]> = new TEvent<T[]>();
    protected _storage: IStorage<{ [key: string]: T[] }>;

    constructor(storage: IStorage<any> = new InMemoryStorage<{ [key: string]: T[] }>()) {
        super();
        this._storage = storage;
    }

    public useStoreWithAction = (key: string, action: (key: string) => void, sort?: (item: T) => any) => {
        const [item, setItem] = useState<T[] | null>(key ? this.get(key) : null);
        this.useStore(() => {
            let data = key ? this.get(key) : null;
            setItem(data)
        });
        useEffect(() => {
            if (key) {
                const i = this.get(key);
                if (!i)
                    action(key);
                else
                    setItem(i);
            }
            else
                setItem(null)
        }, [key])
        return item;
    }

    get(key: string) {
        var value = this._storage.get();
        return value && value[key];
    }
    set(key: string, items: T[]) {
        if (this._storage.get() === null)
            this._storage.set({});
        if (!key)
            throw "invalid item";
        this._storage.get()![key] = items;
        this._storage.save();
        this.Changed.emit();
        this._saved.emit(items);
    }
    setMany(elements: { key: string, items: T[] }[]) {
        if (this._storage.get() === null)
            this._storage.set({});
        for (const el of elements) {
            if (!el.key)
                continue;

            this._storage.get()![el.key] = el.items;
        }
        this._storage.save();
        this.Changed.emit();
        this._saved.emit(elements.flatMap(e => e.items));
    }
    delete(key: string): void {
        if (this._storage.get() !== null) {
            delete this._storage.get()![key];
            this._storage.save();
            this.Changed.emit();
        }
    }
    deleteAll(): void {
        this._storage.set({});
        this._storage.save();
        this.Changed.emit();
    }
    flatten() {
        const data = this._storage.get();
        if (data == null)
            return [];

        const result: T[] = [];
        for (const obj of Object.getOwnPropertyNames(data)) {
            const arr = data[obj];
            result.push(...arr);
        }
        return result;
    }

    diff(requestedIds: string[]) {
        const keys = Object.keys(this._storage.get() || []);
        return without(requestedIds, ...keys);
    }

    updateOne(groupKey: string, item: T, idSelector: (item: T) => string) {
        if (this._storage.get() === null)
            this._storage.set({});
        const key = idSelector(item);
        if (!key)
            throw "invalid item";

        const data = this._storage.get()!;

        const items = { ...data }[groupKey];
        if (!items)
            return;

        const index = items.findIndex(x => idSelector(x) == key);
        if (index >= 0) {
            data[groupKey] = [...items.slice(0, index), item, ...items.slice(index + 1)];
        }
        this._storage.set(data);
        this._storage.save();
        this.Changed.emit();
    }
    addOne(groupKey: string, item: T, idSelector: (item: T) => string) {
        if (this._storage.get() === null)
            this._storage.set({});
        const key = idSelector(item);
        if (!key)
            throw "invalid item";

        const data = this._storage.get()!;

        const items = { ...data }[groupKey];
        if (!items)
            return;
        data[groupKey] = [...items, item];
        this._storage.set(data);
        this._storage.save();
        this.Changed.emit();
    }

    deleteOne(groupKey: string, key: string, idSelector: (item: T) => string) {
        if (this._storage.get() === null)
            this._storage.set({});
        if (!key)
            throw "invalid item";

        const data = this._storage.get()!;

        const items = { ...data }[groupKey];
        if (!items)
            return;

        const index = items.findIndex(x => idSelector(x) == key);
        if (index >= 0) {
            data[groupKey] = [...items.slice(0, index), ...items.slice(index + 1)];
        }
        this._storage.set(data);
        this._storage.save();
        this.Changed.emit();
    }

}

export class AbstractPeriodStore<T> extends AbstractStore<T>{
    requests: Period[] = [];
    constructor(idSelector: (t: T) => string, private dateSelector: (t: T) => Date) {
        super(idSelector)
    }

    setForPeriod(p: Period, items: T[]) {
        if (this._storage.get() === null)
            this._storage.set({});
        this.requests.push(p);
        for (let item of items) {
            this._storage.get()![this.idSelector(item)] = item;
        }
        this.Changed.emit();
    }

    getForPeriod(p: Period): T[] | PeriodRequestInfo {
        if (this.requests.length === 0)
            return { toBeRequested: [p] };
        var alreadyRequested = this.requests.some(x => x.containsPeriod(p));
        if (alreadyRequested) {
            let result: T[] = [];
            for (let key in this._storage.get()!) {
                let item = this._storage.get()![key];
                let date = this.dateSelector(item);
                if (p.contains(date))
                    result.push(item);
            }
            return result;
        }
        let missingPeriods: Period[] = [];
        for (var requested of this.requests) {
            p.subtract(requested).forEach(p => missingPeriods.push(p), this);
        }

        return { toBeRequested: missingPeriods };
    }

    private isPeriodRequestInfo(item: T[] | PeriodRequestInfo): item is PeriodRequestInfo {
        return (item as PeriodRequestInfo).toBeRequested != null;
    }

    public useForPeriodWithActions = (action: (period: Period) => void, period: Period | null) => {
        const forPeriod = period ? this.getForPeriod(period) : [];
        const [item, setItem] = useState<T[] | null>(this.isPeriodRequestInfo(forPeriod) ? null : forPeriod);
        this.useStore(() => {
            let data = period ? this.getForPeriod(period) : [];
            if (!this.isPeriodRequestInfo(data))
                setItem(data)
            else
                setItem(null)
        });
        useEffect(() => {
            if (this.isPeriodRequestInfo(forPeriod)) {
                Promise.all(forPeriod.toBeRequested.map(x => action(x)))
            }
            else {
                setItem(forPeriod)
            }
        }, [period?.from.valueOf(), period?.to.valueOf()])
        return item;
    }
}

export abstract class QueryStore<T>{
    Changed: Event = new Event();
    protected _storage: IStorage<{ queries: string[], items: { [key: string]: T } }>;

    constructor(private idSelector: (t: T) => string, storage: IStorage<any> = new InMemoryStorage<{ [key: string]: T[] }>()) {
        this._storage = storage;
    }
    protected items() {
        var value = this._storage.get();
        return value && Object.keys(value.items).map(key => value!.items[key]) || [];
    }
    get(key: string) {
        var value = this._storage.get();
        return value && value.items[key];
    }
    hasQuery(query: string) {
        const value = this._storage.get();
        return value && (value.queries.indexOf(query) > -1 || value.queries.indexOf("") > -1) || false;
    }
    setMany(items: T[]) {
        if (this._storage.get() === null)
            this._storage.set({ items: {}, queries: [] });
        const store = this._storage.get()!.items;
        for (const item of items) {
            store[this.idSelector(item)] = item;
        }
        this._storage.save();
        this.Changed.emit();
    }
    set(item: T) {
        if (this._storage.get() === null)
            this._storage.set({ items: {}, queries: [] });
        const store = this._storage.get()!.items;
        store[this.idSelector(item)] = item;
        this._storage.save();
        this.Changed.emit();
    }
    addQueryAll() {
        if (this._storage.get() === null)
            this._storage.set({ items: {}, queries: [] });
        this._storage.get()!.queries.push("");
        this._storage.save();
    }
    addQuery(query: string) {
        if (this._storage.get() === null)
            this._storage.set({ items: {}, queries: [] });
        if (!query || query === "") {
            throw "argument exception";
        }
        this._storage.get()!.queries.push(query);
        this._storage.save();
    }
    delete(key: string): void {
        if (this._storage.get() !== null) {
            delete this._storage.get()!.items[key];
            this._storage.save();
            this.Changed.emit();
        }
    }

    hasQueryAll() {
        return this.hasQuery("");
    }
    clear() {
        if (this._storage.get() != null)
            this._storage.set({ items: {}, queries: [] });
    }

}
